"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { Play } from "lucide-react";
import { motion, AnimatePresence, type Variants } from "framer-motion";
import { mediaUrl } from "@/lib/utils";

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  imageUrl: string;
  mobileImageUrl?: string;
  linkUrl?: string;
  buttonText?: string;
  imageAnimation?: string;
  textAnimation?: string;
  focalX?: number;
  focalY?: number;
  mobileFocalX?: number;
  mobileFocalY?: number;
}

interface HeroSliderProps {
  slides: Slide[];
}

// ── Görsel geçiş varyantları ──────────────────────────────────────────────────

const imageFade: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.8, ease: "easeInOut" } },
  exit:  { opacity: 0, transition: { duration: 0.8, ease: "easeInOut" } },
};

const imageSlide: Variants = {
  enter: (dir: number) => ({
    x: dir > 0 ? "100%" : "-100%",
    opacity: 1,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.7, ease: [0.32, 0.72, 0, 1] },
  },
  exit: (dir: number) => ({
    x: dir > 0 ? "-100%" : "100%",
    opacity: 1,
    transition: { duration: 0.7, ease: [0.32, 0.72, 0, 1] },
  }),
};

// Ken Burns: görsel geçişi fade, ama aktif görsel üzerinde CSS animation çalışır
const imageKenBurns: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.9, ease: "easeInOut" } },
  exit:  { opacity: 0, transition: { duration: 0.6, ease: "easeInOut" } },
};

function getImageVariants(type?: string): Variants {
  if (type === "slide")     return imageSlide;
  if (type === "ken-burns") return imageKenBurns;
  return imageFade;
}

// ── Metin giriş varyantları ───────────────────────────────────────────────────

const textVariants: Record<string, Variants> = {
  "fade-up": {
    enter: { opacity: 0, y: 40 },
    center: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.15 } },
    exit:   { opacity: 0, y: -20, transition: { duration: 0.3, ease: "easeIn" } },
  },
  "fade-down": {
    enter: { opacity: 0, y: -40 },
    center: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.15 } },
    exit:   { opacity: 0, y: 20, transition: { duration: 0.3, ease: "easeIn" } },
  },
  "fade-left": {
    enter: { opacity: 0, x: -50 },
    center: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.15 } },
    exit:   { opacity: 0, x: 30, transition: { duration: 0.3, ease: "easeIn" } },
  },
  "fade-right": {
    enter: { opacity: 0, x: 50 },
    center: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.15 } },
    exit:   { opacity: 0, x: -30, transition: { duration: 0.3, ease: "easeIn" } },
  },
  zoom: {
    enter: { opacity: 0, scale: 0.85 },
    center: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut", delay: 0.15 } },
    exit:   { opacity: 0, scale: 1.08, transition: { duration: 0.3, ease: "easeIn" } },
  },
  none: {
    enter: { opacity: 1 },
    center: { opacity: 1 },
    exit:   { opacity: 1 },
  },
};

function getTextVariants(type?: string): Variants {
  return textVariants[type ?? "fade-up"] ?? textVariants["fade-up"];
}

// ── Bileşen ───────────────────────────────────────────────────────────────────

export function HeroSlider({ slides }: HeroSliderProps) {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((idx: number, dir: number) => {
    setDirection(dir);
    setCurrent(idx);
  }, []);

  const prev = useCallback(
    () => goTo((current - 1 + slides.length) % slides.length, -1),
    [current, slides.length, goTo]
  );
  const next = useCallback(
    () => goTo((current + 1) % slides.length, 1),
    [current, slides.length, goTo]
  );

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next, paused, slides.length]);

  if (slides.length === 0) return null;

  const slide = slides[current];
  const imgAnim = slide.imageAnimation ?? "fade";
  const txtAnim = slide.textAnimation ?? "fade-up";

  return (
    <div
      className="relative rounded-[var(--sf-radius)] overflow-hidden aspect-[3/2] sm:aspect-[16/9] md:aspect-[16/8]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Görsel katmanı */}
      <AnimatePresence mode="sync" initial={false} custom={direction}>
        <motion.div
          key={slide.id}
          custom={direction}
          variants={getImageVariants(imgAnim)}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0"
          style={{ zIndex: 1 }}
        >
          {/* Mobil görsel */}
          {slide.mobileImageUrl && (
            <Image
              src={mediaUrl(slide.mobileImageUrl)}
              alt={slide.title}
              fill
              sizes="100vw"
              priority={current === 0}
              style={{ objectPosition: `${slide.mobileFocalX ?? 50}% ${slide.mobileFocalY ?? 50}%` }}
              className={`block object-cover sm:hidden${imgAnim === "ken-burns" ? " animate-ken-burns" : ""}`}
            />
          )}
          {/* Masaüstü görsel */}
          <Image
            src={mediaUrl(slide.imageUrl)}
            alt={slide.title}
            fill
            sizes="100vw"
            priority={current === 0}
            style={{ objectPosition: `${slide.focalX ?? 50}% ${slide.focalY ?? 50}%` }}
            className={`${slide.mobileImageUrl ? "hidden sm:block" : "block"} object-cover${imgAnim === "ken-burns" ? " animate-ken-burns" : ""}`}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Metin katmanı */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={slide.id + "-text"}
          variants={getTextVariants(txtAnim)}
          initial="enter"
          animate="center"
          exit="exit"
          className="absolute inset-0 flex items-center"
          style={{ zIndex: 10 }}
        >
          <div className="px-6 sm:px-8 md:px-16 max-w-2xl">
            {slide.subtitle && (
              <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] mb-3 sm:mb-5" style={{ color: "var(--sf-hero-subtitle)" }}>
                {slide.subtitle}
              </p>
            )}
            <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold mb-3 sm:mb-5 leading-[1.1] whitespace-pre-line" style={{ color: "var(--sf-hero-title)", fontFamily: "var(--sf-font-heading)" }}>
              {slide.title}
            </h1>
            {slide.description && (
              <p className="hidden sm:block text-base md:text-lg mb-6 sm:mb-8 font-light leading-relaxed max-w-md" style={{ color: "var(--sf-hero-subtitle)" }}>
                {slide.description}
              </p>
            )}
            {slide.linkUrl && (
              <Link
                href={slide.linkUrl}
                className="group inline-flex items-center gap-2 px-5 sm:px-7 py-2.5 sm:py-3.5 text-sm sm:text-base font-semibold rounded-[var(--sf-radius)] transition-opacity hover:opacity-90"
                style={{ backgroundColor: "var(--sf-hero-btn-bg)", color: "var(--sf-hero-btn-text)", fontFamily: "var(--sf-font-heading)" }}
              >
                {slide.buttonText || "İncele"}
                <Play className="h-3.5 w-3.5 fill-current stroke-0 transition-transform group-hover:translate-x-0.5" />
              </Link>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Ok butonları */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
            aria-label="Önceki"
          >
            <Play className="h-4 w-4 rotate-180 fill-current stroke-0" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-black/30 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
            aria-label="Sonraki"
          >
            <Play className="h-4 w-4 fill-current stroke-0" />
          </button>

          {/* Nokta indikatörleri */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > current ? 1 : -1)}
                className="flex h-9 w-6 items-center justify-center"
                style={{ touchAction: "manipulation" }}
                aria-label={`Slide ${i + 1}`}
                aria-current={i === current}
              >
                <span
                  className="block rounded-full transition-all duration-300"
                  style={{
                    width: i === current ? 20 : 6,
                    height: 6,
                    backgroundColor: i === current ? "var(--sf-accent)" : "rgba(255,255,255,0.5)",
                  }}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
